import { useEffect } from 'react';
import { useUrlHash, useUrlQuery } from '../../Helper/utils/utils';
import {
    EAppleResponseType,
    ELoginEvents,
    ELoginMethods,
    ELoginMode,
    ESessionStorageKeys
} from '../../Helper/enums';
import { TokenResponse } from '@react-oauth/google';
import GoogleLogin from '../GoogleLogin';
import FacebookLogin from '../FacebookLogin';
import AppleLogin from 'react-apple-login';
import LoginButton from '../LoginButton';
import { ProvidersModeProps } from '../types';
import { isDesktop } from 'react-device-detect';

function ProvidersMode({
    setMode,
    stopLoader,
    startLoader,
    loginResponse,
    playerAuthData,
    loginButtonClickWrapper,
    otpButton,
    trackLoginEvent,
    translations
}: ProvidersModeProps) {
    const isAppleRedirect = useUrlQuery('apple') === 'true';
    const facebookAccessToken = useUrlHash('#access_token');
    const appleIdToken = useUrlHash('id_token');
    const showOtpButton =
        playerAuthData.otpOn &&
        (isDesktop ? playerAuthData?.otpTextModel : true);

    const facebookResponseHandler = () => {
        if (!facebookAccessToken) return;
        startLoader();
        const data = {
            userToken: facebookAccessToken,
            authMethod: ELoginMethods.FACEBOOK
        };
        sessionStorage.removeItem(ESessionStorageKeys.REDIRECT_STATE);
        loginResponse(data);
    };

    const googleResponseHandler = (response: TokenResponse) => {
        if (!response.access_token) return;

        const data = {
            userToken: response.access_token,
            authMethod: ELoginMethods.GOOGLE
        };
        sessionStorage.removeItem(ESessionStorageKeys.REDIRECT_STATE);
        loginResponse(data);
    };

    const appleResponseHandler = (response: any) => {
        const token = response.code || response;
        if (token) {
            const data = {
                userToken: token,
                authMethod: ELoginMethods.APPLE
            };
            sessionStorage.removeItem(ESessionStorageKeys.REDIRECT_STATE);
            loginResponse(data);
        }
    };

    const loginButtonClicked = (
        callback: Function,
        method: ELoginMethods,
        event?: string
    ) => {
        loginButtonClickWrapper(callback, method, event);
    };

    useEffect(() => {
        if (isAppleRedirect) startLoader();
        if (facebookAccessToken) facebookResponseHandler();
        if (appleIdToken) appleResponseHandler(appleIdToken);
        return () => {
            stopLoader();
        };
    }, [isAppleRedirect, facebookAccessToken, appleIdToken]);

    const isWebView = () => {
        const userAgent = navigator.userAgent;
        return (
            (userAgent.indexOf('Instagram') ||
                userAgent.indexOf('FBAN') > -1 ||
                userAgent.indexOf('FBAV')) > -1
        );
    };

    useEffect(() => {
        if (isWebView()) {
            playerAuthData.appleOn = false;
            playerAuthData.googleOn = false;
        }
    }, [playerAuthData]);

    return (
        <>
            {showOtpButton && (
                <LoginButton
                    textColor={otpButton?.textColor}
                    icon={otpButton?.icon}
                    backgroundColor={otpButton?.backgroundColor}
                    text={otpButton?.text}
                    callback={() =>
                        loginButtonClicked(
                            () => setMode(ELoginMode.OTP),
                            ELoginMethods.OTP,
                            ELoginEvents.OTP_BUTTON_CLICKED
                        )
                    }
                    dataTestId="otp-login-button"
                />
            )}
            {playerAuthData.fbOn && playerAuthData.fbAppId && (
                <FacebookLogin
                    appId={playerAuthData.fbAppId}
                    loginButtonClickWrapper={loginButtonClickWrapper}
                    translations={translations}
                />
            )}
            {playerAuthData.googleOn && playerAuthData.googleAppId && (
                <GoogleLogin
                    stopLoader={stopLoader}
                    loginButtonClickWrapper={loginButtonClickWrapper}
                    callback={googleResponseHandler}
                    trackLoginEvent={trackLoginEvent}
                    translations={translations}
                />
            )}
            {playerAuthData.appleOn && playerAuthData.appleAppId && (
                <AppleLogin
                    clientId={playerAuthData.appleAppId}
                    redirectURI={`${window.location.href}?apple=true`}
                    responseType={
                        playerAuthData.appleResponseType ===
                        EAppleResponseType.CODE
                            ? EAppleResponseType.CODE
                            : EAppleResponseType.ID_TOKEN
                    }
                    responseMode={
                        playerAuthData.appleResponseType ===
                        EAppleResponseType.CODE
                            ? 'query'
                            : 'fragment'
                    }
                    callback={appleResponseHandler}
                    render={(renderProps) => (
                        <LoginButton
                            variant="apple"
                            callback={() =>
                                loginButtonClicked(
                                    renderProps.onClick,
                                    ELoginMethods.APPLE
                                )
                            }
                            text={translations.signInWithApple}
                        >
                            <img
                                src="https://media.appcharge.com/defaults/apple_icon_white.svg"
                                alt="apple icon"
                            />
                        </LoginButton>
                    )}
                />
            )}
            {playerAuthData.usernamePasswordOn && (
                <LoginButton
                    variant="transparent"
                    text={translations.loginWithUsername}
                    callback={() =>
                        loginButtonClicked(
                            () => setMode(ELoginMode.USERNAME),
                            ELoginMethods.USERNAME
                        )
                    }
                />
            )}
            {playerAuthData.userTokenOn && (
                <LoginButton
                    variant="transparent"
                    text={translations.loginWithPlayerId}
                    callback={() =>
                        loginButtonClicked(
                            () => setMode(ELoginMode.TOKEN_ID),
                            ELoginMethods.TOKEN
                        )
                    }
                />
            )}
        </>
    );
}

export default ProvidersMode;
